import React, { useState } from 'react';
import { FiUser, FiMail, FiPhone, FiSave, FiUpload, FiLock } from 'react-icons/fi';
import { auth as authApi, users as usersApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ first_name: user?.first_name || '', last_name: user?.last_name || '', phone: user?.phone || '' });
  const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authApi.updateProfile(form);
      updateUser(res.data.data);
      toast.success('Profile updated');
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const handlePwd = async (e) => {
    e.preventDefault();
    if (pwdForm.newPassword !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    setSavingPwd(true);
    try {
      await authApi.changePassword({ currentPassword: pwdForm.currentPassword, newPassword: pwdForm.newPassword });
      toast.success('Password changed');
      setPwdForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSavingPwd(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const res = await usersApi.uploadPhoto(file);
      updateUser({ profile_photo_url: res.data.data.profile_photo_url });
      toast.success('Photo updated');
    } catch { toast.error('Upload failed'); }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await usersApi.uploadResume(file);
      toast.success('Resume uploaded');
    } catch { toast.error('Upload failed'); }
  };

  return (
    <div className="space-y-5 max-w-xl animate-fade-in">
      <h1 className="text-2xl font-black text-gray-900">Profile</h1>

      {/* Avatar */}
      <div className="card p-5 flex items-center gap-5">
        <div className="relative">
          <div className="w-20 h-20 rounded-2xl bg-navy/10 flex items-center justify-center overflow-hidden">
            {user?.profile_photo_url ? (
              <img src={user.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <span className="text-navy text-2xl font-black">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
            )}
          </div>
        </div>
        <div>
          <h3 className="font-bold text-gray-900">{user?.first_name} {user?.last_name}</h3>
          <p className="text-sm text-gray-400">{user?.email}</p>
          <div className="flex gap-2 mt-2">
            <label className="btn-secondary text-xs py-1.5 cursor-pointer">
              <FiUpload size={12} /> Photo
              <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            </label>
            <label className="btn-secondary text-xs py-1.5 cursor-pointer">
              <FiUpload size={12} /> Resume
              <input type="file" accept=".pdf" className="hidden" onChange={handleResumeUpload} />
            </label>
          </div>
        </div>
      </div>

      {/* Personal info */}
      <div className="card p-5">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><FiUser size={16} /> Personal Information</h3>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">First Name</label><input className="input" value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} required /></div>
            <div><label className="label">Last Name</label><input className="input" value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} required /></div>
          </div>
          <div>
            <label className="label flex items-center gap-1"><FiMail size={12} /> Email</label>
            <input className="input bg-gray-50" value={user?.email} disabled />
            <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
          </div>
          <div>
            <label className="label flex items-center gap-1"><FiPhone size={12} /> Phone</label>
            <input className="input" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+91 9876543210" />
          </div>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FiSave size={14} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Change password */}
      <div className="card p-5">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><FiLock size={16} /> Change Password</h3>
        <form onSubmit={handlePwd} className="space-y-4">
          <div><label className="label">Current Password</label><input type="password" className="input" value={pwdForm.currentPassword} onChange={e => setPwdForm(f => ({...f, currentPassword: e.target.value}))} required /></div>
          <div><label className="label">New Password</label><input type="password" className="input" value={pwdForm.newPassword} onChange={e => setPwdForm(f => ({...f, newPassword: e.target.value}))} required minLength={8} /></div>
          <div><label className="label">Confirm New Password</label><input type="password" className="input" value={pwdForm.confirm} onChange={e => setPwdForm(f => ({...f, confirm: e.target.value}))} required /></div>
          <button type="submit" disabled={savingPwd} className="btn-primary">
            {savingPwd ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
